import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { liveSuggestMatches, looksLikeOem } from "@/lib/shop-search";
import { mapDbProducts, productImageFor, publishedProductsQuery } from "@/lib/shop-catalog";
import { SearchSuggest } from "./SearchSuggest";
import { Button } from "@/components/ui/button";

export interface NavSearchProps {
  autoFocus?: boolean;
  /** Starting value (e.g. the current shop query). */
  initialValue?: string;
  /** Extra classes for the input (sizing differs between desktop bar and mobile sheet). */
  inputClassName?: string;
  /** Called after any navigation (pick, submit, show more) so parents can close sheets. */
  onNavigate?: () => void;
}

/**
 * Compact live search for the site header. Same behaviour as the shop hero
 * search: suggestions appear while typing, keyword + OEM/SKU are matched at
 * once, arrows/Enter/Esc work, and "Show all" opens the full result list.
 */
export function NavSearch({ autoFocus, initialValue = "", inputClassName, onNavigate }: NavSearchProps) {
  const [value, setValue] = useState(initialValue);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const suggestionsId = useId();
  const navigate = useNavigate();

  const { data } = useQuery(publishedProductsQuery);
  const catalog = useMemo(() => mapDbProducts(data), [data]);

  const qTrim = value.trim();
  const oemLike = looksLikeOem(qTrim);
  const suggest = useMemo(() => liveSuggestMatches(catalog, qTrim), [catalog, qTrim]);
  const showSuggest = open && qTrim.length >= 2 && suggest.items.length > 0;

  useEffect(() => setActiveIndex(0), [qTrim]);

  // Close the dropdown on outside click/tap.
  useEffect(() => {
    if (!open) return;
    const handler = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [open]);

  const goToShop = () => {
    const q = qTrim;
    if (!q) return;
    setOpen(false);
    onNavigate?.();
    navigate({
      to: "/shop",
      search: { q, mode: oemLike ? "oem" : "keyword" },
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggest) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.min(suggest.items.length, 2) - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "Enter") {
      // Enter opens the product only when it is the single match; otherwise
      // the form submits and the full result list is shown.
      if (suggest.total === 1 && suggest.items[0]?.product.slug) {
        e.preventDefault();
        setOpen(false);
        onNavigate?.();
        navigate({ to: "/p/$slug", params: { slug: suggest.items[0].product.slug } });
      }
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        goToShop();
      }}
      className="w-full"
    >
      <div ref={wrapRef} className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          aria-label="Search parts by name, SKU or OEM number"
          placeholder="Search by part, SKU or OEM…"
          value={value}
          autoFocus={autoFocus}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => {
            setValue(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          aria-controls={showSuggest ? suggestionsId : undefined}
          className={
            inputClassName ??
            "h-10 w-full appearance-none rounded-xl border border-border bg-secondary/60 pl-9 pr-8 text-sm outline-none transition-colors focus:border-ring focus:bg-background"
          }
        />
        {value && (
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={() => setValue("")}
            aria-label="Clear search"
            className="press absolute right-1.5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-lg text-muted-foreground hover:bg-secondary"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
        {showSuggest && (
          <SearchSuggest
            id={suggestionsId}
            matches={suggest.items}
            totalCount={suggest.total}
            oemLike={oemLike}
            activeIndex={activeIndex}
            onActiveIndex={setActiveIndex}
            productImageFor={productImageFor}
            onClose={() => setOpen(false)}
            onShowMore={goToShop}
          />
        )}
        <span className="sr-only" role="status" aria-atomic="true">
          {open && qTrim.length >= 2 ? `${suggest.total} search results` : ""}
        </span>
      </div>
    </form>
  );
}
